import * as React from "react";
import _ from "lodash";
import { Paper, createStyles, Theme, makeStyles } from "@material-ui/core";
import {
  AddShoppingCart as PickupListIcon,
  Archive as ArchiveIcon,
  Print as PrintIcon,
  Cached as ReintegrateIcon,
} from "@material-ui/icons";
import { AnvokError } from "lib";
import { useErrorHandler } from "../ErrorHandler";
import CreatePicking from "./orderlist/CreatePicking";
import OrderRow from "./orderlist/OrderRow";
import { FixedSizeList, areEqual, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import UpdateService from "./orderlist/UpdateService";
import useRouter from "use-react-router";
import { FilterMenuValue } from "./components/FilterMenu";
import OrderHeader, { SortValue } from "./orderlist/OrderHeader";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@material-ui/lab";
import OrderDetailsDialog from "./orderlist/OrderDetailsDialog";
import { useApiClient, useMessagesClient } from "lib/store/useStore";
import { ApiOrder } from "lib/api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    content: {
      flexGrow: 1,
      overflowX: "auto",
      overflowY: "hidden",
    },
    footer: {
      padding: theme.spacing(1),
    },
    speeddial: {
      position: "absolute",
      bottom: theme.spacing(4),
      right: theme.spacing(4),
    },
  })
);

interface Params {
  id?: string;
}

function itemKey(index: number, data: any) {
  return data.orders[index]._id;
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

const Row = React.memo((props: ListChildComponentProps) => {
  const { index, style, data } = props;
  const order: ApiOrder = data.orders[index];
  const id: string = order._id;
  const selected: boolean = data.selected.indexOf(id) > -1;
  const loading: boolean = data.loadingOrders.indexOf(id) > -1;

  return (
    <div style={style}>
      <OrderRow
        order={order}
        selected={selected}
        loading={loading}
        onChange={data.handleChange}
        onCarrierChange={data.handleCarrierChange}
        onPrint={data.handlePrint}
      />
    </div>
  );
}, areEqual);

const OrderList: React.FunctionComponent<{}> = () => {
  const api = useApiClient();
  const msg = useMessagesClient();
  const [loading, setLoading] = React.useState("Chargement des commandes...");
  const [speedDialOpen, setSpeedDialOpen] = React.useState(false);
  const [orders, setOrders] = React.useState<ApiOrder[]>([]);
  const [order, setOrder] = React.useState<ApiOrder>();
  const [printOrders, setPrintOrders] = React.useState<ApiOrder[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [loadingOrders, setLoadingOrders] = React.useState<string[]>([]);
  const [pickuplist, setPickuplist] = React.useState<string[]>([]);
  const [filter, setFilter] = React.useState<FilterMenuValue>({
    state0: false,
    state10: true,
    state20: true,
    state30: true,
    state50: false,
    date: null,
    dateFrom: null,
    dateTo: null,
  });
  const [sort, setSort] = React.useState<SortValue>({
    key: "date",
    state: "desc",
  });
  const [search, setSearch] = React.useState("");
  const { match, history } = useRouter<Params>();
  const errorHandler = useErrorHandler();
  const classes = useStyles();

  const handleUpdate = React.useCallback((orders: ApiOrder[]) => {
    const updatedIds: string[] = [];
    setOrders((current) => {
      orders.forEach((order) => {
        const idx = current.findIndex((o) => {
          return o._id === order._id;
        });
        updatedIds.push(order._id);
        if (idx > -1) {
          current[idx] = order;
        }
      });
      return [...current];
    });
    if (updatedIds.length > 0) {
      setLoadingOrders((c) => {
        return c.filter((v) => updatedIds.indexOf(v) === -1);
      });
    }
  }, []);

  React.useEffect(() => {
    const idx = orders.findIndex((o) => {
      return o._id === match.params.id;
    });
    setOrder(idx > -1 ? orders[idx] : undefined);
  }, [match.params.id, orders]);

  const handleClosePickupList = React.useCallback(() => {
    setPickuplist([]);
  }, []);

  const handleSpeedDialClose = function (
    event: React.SyntheticEvent<{}>,
    reason: string
  ) {
    setSpeedDialOpen(false);
    if (reason === "toggle") {
      setPickuplist(selected);
    }
  };

  const handlePrintClick = React.useCallback(() => {
    setSpeedDialOpen(false);
    setPrintOrders(orders.filter((v) => selected.indexOf(v._id) > -1));
  }, [orders, selected]);

  const handleArchiveClick = React.useCallback(() => {
    setLoading(`Mise à jour de ${selected.length} commandes`);
    api
      .PUT("/api/orders", {
        body: { ids: selected, fields: { archive: true } },
      })
      .then(() => {
        setOrders((c) => c.filter((v) => selected.indexOf(v._id) === -1));
        setSelected([]);
      })
      .catch(errorHandler)
      .finally(() => setLoading(""));
  }, [errorHandler, api, selected]);

  const handleReintegrateClick = React.useCallback(() => {
    if (selected.length <= 0) return;
    setLoading(`Mise à jour de ${selected.length} commandes`);
    api
      .PUT("/api/orders", { body: { ids: selected, fields: { state: 0 } } })
      .catch(errorHandler)
      .finally(() => setLoading(""));
  }, [errorHandler, api, selected]);

  const handleChange = React.useCallback((id: string, selected: boolean) => {
    if (selected) {
      setSelected((c) => [...c, id]);
    } else {
      setSelected((c) => c.filter((v) => v !== id));
    }
  }, []);

  const handleCarrierChange = React.useCallback(
    (id: string, value: string) => {
      setLoadingOrders((c) => [...c, id]);
      setOrders((current) => {
        const idx = current.findIndex((o) => {
          return o._id === id;
        });
        if (current[idx].internal) {
          current[idx].internal!.carrier = value;
        }
        return [...current];
      });
      api.PUT("/api/orders", {
        body: { ids: [id], fields: { "internal.carrier": value } },
      });
    },
    [api]
  );

  const handlePrint = React.useCallback(
    (id: string) => {
      const idx = orders.findIndex((o) => {
        return o._id === id;
      });
      setPrintOrders(idx > -1 ? [orders[idx]] : []);
    },
    [orders]
  );

  const handleSelectAllChange = React.useCallback(
    (checked: boolean) => {
      setSelected((c) => (checked ? orders.map((o) => o._id) : []));
    },
    [orders]
  );

  React.useEffect(() => {
    setLoading("Chargement des commandes...");
    const states: number[] = _.map(filter, function (v, k) {
      if (k.startsWith("state") && v) {
        return parseInt(k.substring(5));
      }
    }).filter(notEmpty);
    api
      .GET("/api/orders", {
        params: {
          query: {
            sortBy: sort.key,
            sortDir: sort.state,
            states,
            dateFrom: filter.dateFrom
              ? filter.dateFrom.toISOString()
              : undefined,
            dateTo: filter.dateTo ? filter.dateTo.toISOString() : undefined,
          },
        },
      })
      .then((r) => {
        if (r.error) {
          throw new AnvokError(r.error.message);
        }
        setOrders(r.data.items);
        setLoading("");
      })
      .catch(errorHandler);
  }, [api, errorHandler, handleUpdate, filter, sort]);

  React.useEffect(() => {
    const svc = new UpdateService(api, handleUpdate);
    msg.onOrderUpdated((msg) => {
      setLoadingOrders((c) => [...c, msg.id]);
      svc.update(msg.id);
    });
    return () => {
      msg.onOrderUpdated(undefined);
      svc.dispose();
    };
  }, [api, handleUpdate, msg]);

  const data = React.useMemo(() => {
    return {
      orders:
        search.length > 0
          ? orders.filter((o) => {
              const s = search.toLowerCase();
              return (
                o.ref.toLowerCase().indexOf(s) > -1 ||
                (o.sourceId && o.sourceId.toLowerCase().indexOf(s) > -1) ||
                o.livraison!.nom.toLowerCase().indexOf(s) > -1 ||
                (o.refClient && o.refClient.toLowerCase().indexOf(s) > -1)
              );
            })
          : orders,
      selected,
      loadingOrders,
      handleChange,
      handleCarrierChange,
      handlePrint,
    };
  }, [
    orders,
    selected,
    loadingOrders,
    search,
    handleChange,
    handleCarrierChange,
    handlePrint,
  ]);

  return (
    <Paper elevation={1} className={classes.root}>
      <>
        <div className={classes.content}>
          <AutoSizer>
            {({ width, height }) => {
              width = width < 1100 ? 1100 : width;
              return (
                <>
                  <OrderHeader
                    count={orders.length}
                    selected={selected.length}
                    loading={loading}
                    sort={sort}
                    search={search}
                    onSelectAllChange={handleSelectAllChange}
                    onFilterChange={(v) => setFilter(v)}
                    onSortChange={(v) => setSort(v)}
                    onSearchChange={(v) => setSearch(v)}
                    style={{ width }}
                  />
                  <FixedSizeList
                    itemCount={data.orders.length}
                    itemData={data}
                    itemSize={95}
                    height={height - 47}
                    width={width}
                    itemKey={itemKey}
                  >
                    {Row}
                  </FixedSizeList>
                </>
              );
            }}
          </AutoSizer>
        </div>
        <SpeedDial
          className={classes.speeddial}
          icon={<SpeedDialIcon icon={<PickupListIcon />} />}
          hidden={selected.length < 1}
          onOpen={() => setSpeedDialOpen(true)}
          onClose={handleSpeedDialClose}
          open={speedDialOpen}
          ariaLabel="actions"
        >
          <SpeedDialAction
            icon={<PrintIcon />}
            tooltipTitle="Imprimer"
            onClick={handlePrintClick}
          />
          <SpeedDialAction
            icon={<ArchiveIcon />}
            tooltipTitle="Archiver"
            onClick={handleArchiveClick}
          />
          <SpeedDialAction
            icon={<ReintegrateIcon />}
            tooltipTitle="Remettre en intégration"
            onClick={handleReintegrateClick}
          />
        </SpeedDial>
        {pickuplist.length > 0 && (
          <CreatePicking ids={pickuplist} onClose={handleClosePickupList} />
        )}
      </>
      <OrderDetailsDialog
        orders={order ? [order] : []}
        onClose={() => history.push("/orders")}
      />
      {printOrders.length > 0 && (
        <OrderDetailsDialog
          orders={printOrders}
          print={true}
          onClose={() => {
            setPrintOrders([]);
          }}
        />
      )}
    </Paper>
  );
};

export default OrderList;
